import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Clock, User, CheckSquare, AlertTriangle, Trash2, ChevronLeft, ChevronRight } from 'react-feather';
import DailyAppointmentsList from './DailyAppointmentList';
import './styles/calendar.css';

function AppointmentCalendar({ appointments, onStatusChange, onDelete }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayAppointments, setDayAppointments] = useState([]);

  // Get appointments for the selected date
  useEffect(() => {
    if (selectedDate) {
      const dateString = selectedDate.toISOString().split('T')[0];
      const filteredAppointments = appointments.filter(
        appointment => appointment.date === dateString
      );
      setDayAppointments(filteredAppointments);
    } else {
      setDayAppointments([]);
    }
  }, [selectedDate, appointments]);

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get day of week for first day of month (0 = Sunday, 6 = Saturday)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Get month name
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Render calendar header (day names)
  const renderDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days.map(day => (
      <div key={day} className="calendar-day-name">
        {day}
      </div>
    ));
  };

  // Check if a date has appointments
  const hasAppointments = (date) => {
    const dateString = date.toISOString().split('T')[0];
    return appointments.some(appointment => appointment.date === dateString);
  };

  // Render calendar cells
  const renderCells = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month);
    
    const rows = [];
    let cells = [];
    
    // Fill in leading empty cells for days from previous month
    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(
        <div key={`empty-${i}`} className="calendar-day empty"></div>
      );
    }
    
    // Fill in days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isSelected = selectedDate && 
                         date.getDate() === selectedDate.getDate() && 
                         date.getMonth() === selectedDate.getMonth() && 
                         date.getFullYear() === selectedDate.getFullYear();
      
      const isToday = new Date().toDateString() === date.toDateString();
      const hasEvents = hasAppointments(date);
      
      cells.push(
        <div 
          key={day} 
          className={`calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          <div className="day-number">{day}</div>
          {hasEvents && <div className="event-indicator"></div>}
        </div>
      );
      
      // Start a new row after 7 cells (end of week)
      if ((firstDayOfMonth + day) % 7 === 0 || day === daysInMonth) {
        rows.push(
          <div key={day} className="calendar-row">
            {cells}
          </div>
        );
        cells = [];
      }
    }
    
    return rows;
  };

  // Get status color class
  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed':
        return 'status-confirmed';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  return (
    <div className="appointment-calendar-container">
      <div className="calendar-section">
        <div className="calendar-header">
          <h2>Appointment Calendar</h2>
          <div className="calendar-navigation">
            <button onClick={prevMonth} className="nav-button">
              <ChevronLeft />
            </button>
            <h3>{monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}</h3>
            <button onClick={nextMonth} className="nav-button">
              <ChevronRight />
            </button>
          </div>
        </div>
        
        <div className="calendar-grid">
          {renderDays()}
          {renderCells()}
        </div>
      </div>
      
      <div className="day-appointments-section">
        {selectedDate ? (
          // Use our new mobile-friendly component
          <DailyAppointmentsList 
            appointments={dayAppointments}
            date={selectedDate}
            onStatusChange={onStatusChange}
            onDelete={onDelete}
          />
        ) : (
          <div className="select-day-prompt">
            <CalendarIcon size={32} />
            <p>Select a day to view appointments</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default AppointmentCalendar;